/**
 * src/engine/index.ts — public engine surface. `project()` assembles the full
 * ProjectionResult (borrow path + sell path + estate). All functions are pure.
 */
import type { ProjectionResult, ScenarioInputs } from '@/types';
import { runSellPath } from './borrowVsSell';
import { computeEstate } from './estate';
import { type ProjectionOptions, runProjection } from './projection';

/** Run the complete projection: borrow path, sell counterfactual, and estate view. */
export function project(
  inputs: ScenarioInputs,
  opts: ProjectionOptions = {},
): ProjectionResult {
  const borrow = runProjection(inputs, opts);
  const sell = runSellPath(inputs, { startDate: opts.startDate });
  const estate = computeEstate(inputs, borrow, sell);
  return {
    rows: borrow.rows,
    summary: borrow.summary,
    sellPath: { rows: sell.rows, endingNetWorth: sell.endingNetWorth, cumulativeTax: sell.cumulativeTax },
    estate,
  };
}

export { runProjection, containsNonFinite, buildInitialStates } from './projection';
export { runSellPath, sellToNet } from './borrowVsSell';
export { computeEstate } from './estate';
export * from './rates';
export * from './capGainsBrackets';
export * from './gainsTax';
export * from './ratePaths';
export * from './stressPresets';
export * from './tax';
export * from './collateral';
export * from './portfolio';
export * from './assetClasses';
